/**
 * ResultScreen — end-of-run summary for a dynamic game.
 *
 * Submits the run via gamePlayService on mount (idempotent guard),
 * shows score/win state, accuracy, time, and a Leaderboard. CTAs:
 *   - Play again (caller provides callback)
 *   - Back to games (caller provides callback)
 */

import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Trophy, Sparkles, Clock, Target, Flame, AlertCircle, Loader2, Share2 } from 'lucide-react';
import { gamePlayService, type SubmittedSession } from '@/services/gamePlayService';
import { Leaderboard } from './Leaderboard';
import { ShareGameDialog } from './ShareGameDialog';
import type { GameRunResult } from '@/types/gameSpec';

interface ResultScreenProps {
  gameId: string;
  result: GameRunResult;
  onPlayAgain?: () => void;
  onExit?: () => void;
  /** When provided, sent to server for tamper detection (Wave 5). */
  specSnapshotHash?: string;
  /** When the game was opened from a share link, surface the sender for credit. */
  sharedFromUserId?: string | null;
  /** Used in the Share dialog title. Optional — defaults to "this game". */
  gameTitle?: string;
}

export const ResultScreen = ({
  gameId,
  result,
  onPlayAgain,
  onExit,
  specSnapshotHash,
  sharedFromUserId,
  gameTitle,
}: ResultScreenProps) => {
  const submittedRef = useRef(false);
  const [submitted, setSubmitted] = useState<SubmittedSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    gamePlayService
      .submitRun(gameId, result, { specSnapshotHash, sharedFromUserId })
      .then((s) => setSubmitted(s))
      .catch((e) => setError(e.message ?? 'Failed to save your score'))
      .finally(() => setSubmitting(false));
  }, [gameId, result, specSnapshotHash, sharedFromUserId]);

  const score = submitted?.score ?? result.score;
  const maxScore = submitted?.max_score ?? result.max_score;
  const won = submitted?.won ?? result.won;
  const accuracy = result.scene_count
    ? Math.round((result.scenes_correct / result.scene_count) * 100)
    : 0;
  const seconds = Math.round(result.time_spent_ms / 1000);
  const pct = maxScore ? Math.round((score / maxScore) * 100) : 0;

  return (
    <div className="grid gap-4 md:grid-cols-[1fr_minmax(0,360px)]">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl">
              {won ? (
                <>
                  <Sparkles className="h-6 w-6 text-primary" /> Great job!
                </>
              ) : (
                <>
                  <Trophy className="h-6 w-6 text-muted-foreground" /> Run complete
                </>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div>
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-muted-foreground">Score</span>
                <span className="text-3xl font-bold tabular-nums">
                  {score}
                  <span className="text-base text-muted-foreground"> / {maxScore}</span>
                </span>
              </div>
              <Progress value={pct} className="mt-2 h-2" />
            </div>

            <div className="grid grid-cols-3 gap-3 text-center">
              <Stat icon={<Target className="h-4 w-4" />} label="Accuracy" value={`${accuracy}%`} />
              <Stat icon={<Flame className="h-4 w-4" />} label="Best streak" value={String(result.streak_max)} />
              <Stat icon={<Clock className="h-4 w-4" />} label="Time" value={`${seconds}s`} />
            </div>

            <div className="flex flex-wrap gap-2">
              <Badge variant={won ? 'default' : 'secondary'}>
                {won ? 'Passed' : 'Try again'}
              </Badge>
              <Badge variant="outline">
                {result.scenes_correct}/{result.scene_count} scenes correct
              </Badge>
              {submitting && (
                <Badge variant="outline" className="gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" /> Saving…
                </Badge>
              )}
              {error && (
                <Badge variant="destructive" className="gap-1">
                  <AlertCircle className="h-3 w-3" /> {error}
                </Badge>
              )}
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              {onPlayAgain && (
                <Button onClick={onPlayAgain}>Play again</Button>
              )}
              <Button variant="secondary" onClick={() => setShareOpen(true)}>
                <Share2 className="h-4 w-4 mr-2" /> Share
              </Button>
              {onExit && (
                <Button variant="outline" onClick={onExit}>
                  Back to games
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      <Leaderboard gameId={gameId} defaultTab="game" />

      <ShareGameDialog
        gameId={gameId}
        gameTitle={gameTitle ?? 'this game'}
        open={shareOpen}
        onOpenChange={setShareOpen}
      />
    </div>
  );
};

const Stat = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) => (
  <div className="rounded-lg border p-3">
    <div className="flex items-center justify-center gap-1 text-muted-foreground text-xs">
      {icon} {label}
    </div>
    <div className="mt-1 text-lg font-semibold tabular-nums">{value}</div>
  </div>
);

export default ResultScreen;
